import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { MessageService } from '../../../../services/message-service/message.service';
import { StammdatenService } from '../../../../components/stammdaten/stammdaten.service';
import { Gruppe } from '../../../../components/gruppen/classes/gruppe';
import { GruppeTierEinstalldatum } from '../../../../components/gruppe-tier/filter/gruppe-tier-einstalldatum-filter/gruppe-tier-einstalldatum';
import { TierFilterSettings } from '../../../../components/gruppe-tier/filter/gruppe-tier-filter-dialog/classes/tier-filter-settings';
import { HttpParams } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { formatTierMengenLabel } from '../tier-mengen-label';

@Component({
    selector: 'kiene-gruppe-tier-einstalldatum-filter',
    templateUrl: './gruppe-tier-einstalldatum-filter.component.html',
    styleUrls: ['./gruppe-tier-einstalldatum-filter.component.scss'],
})
export class GruppeTierEinstalldatumFilterComponent implements OnInit, OnChanges, OnDestroy {
    sub: Subscription;
    isLoading = true;
    label = 'Lädt...';

    @Input('gruppe') gruppe: Gruppe;
    @Input() gruppeIds: number[] = [];
    @Input('filter') filter: TierFilterSettings;
    @Input('einstalldaten') selectedEinstalldaten: GruppeTierEinstalldatum[] = [];
    @Input('hideOnEmptyList') hideOnEmptyList = false;
    @Input() mitEinstallgewicht = false;

    @Output('change') change = new EventEmitter<GruppeTierEinstalldatum[]>();
    selectedEinstalldatumCtrl: FormControl = new FormControl<GruppeTierEinstalldatum[]>(undefined);

    einstalldaten: GruppeTierEinstalldatum[] = [];

    constructor(
        private messageService: MessageService,
        private stammdatenService: StammdatenService,
    ) {}

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    ngOnInit(): void {
        this.selectedEinstalldatumCtrl.disable({ emitEvent: false });
        this.ladeEinstalldatumFuerGruppe();

        this.selectedEinstalldatumCtrl.setValue(this.selectedEinstalldaten, { emitEvent: false });

        this.selectedEinstalldatumCtrl.valueChanges.subscribe((value: GruppeTierEinstalldatum[]) => {
            this.change.emit(value);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Parent rebinds [einstalldaten] after every selection. Reloading here + enable()
        // re-emitting valueChanges caused an endless load/select loop.
        if (changes['einstalldaten'] && !changes['gruppe'] && !changes['gruppeIds'] && !changes['filter']) {
            if (!changes['einstalldaten'].firstChange) {
                this.selectedEinstalldatumCtrl.setValue(this.selectedEinstalldaten, { emitEvent: false });
                return;
            }
        }
        this.ladeEinstalldatumFuerGruppe();
    }

    mengenLabel(datum: GruppeTierEinstalldatum): string {
        return formatTierMengenLabel(datum);
    }

    compareGruppeTierEinstalldatum = (o1: GruppeTierEinstalldatum, o2: GruppeTierEinstalldatum): boolean => {
        return this.toDateKey(o1?.einstalldatum) === this.toDateKey(o2?.einstalldatum);
    };

    resetCtrl($event: MouseEvent) {
        $event.stopPropagation();
        this.selectedEinstalldatumCtrl.setValue(undefined);
    }

    public update() {
        this.ladeEinstalldatumFuerGruppe();
    }

    public disableControl() {
        this.selectedEinstalldatumCtrl.disable({ emitEvent: false });
    }

    private toDateKey(value: Date | string | null | undefined): string {
        if (value == null || value === '') {
            return '';
        }
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? '' : formatDate(value, 'yyyy-MM-dd', 'de');
        }
        const asDate = new Date(value);
        if (!isNaN(asDate.getTime())) {
            return formatDate(asDate, 'yyyy-MM-dd', 'de');
        }
        return String(value).slice(0, 10);
    }

    ladeEinstalldatumFuerGruppe() {
        const targetGroupIds =
            (this.gruppeIds || []).map(id => Number(id)).filter(id => id > 0).length > 0
                ? (this.gruppeIds || []).map(id => Number(id)).filter(id => id > 0)
                : this.gruppe?.gruppe_id
                  ? [Number(this.gruppe.gruppe_id)]
                  : [];
        if (targetGroupIds.length > 0) {
            this.isLoading = true;
            let params = new HttpParams();
            if (this.filter && this.filter.viehhaendler && this.filter.viehhaendler.length > 0) {
                for (const h of this.filter.viehhaendler) {
                    params = params.append('viehhaendler_id[]', h.viehhaendler_id);
                }
            }
            if (this.filter && this.filter.betriebskontakte && this.filter.betriebskontakte.length > 0) {
                for (const kontakt of this.filter.betriebskontakte) {
                    params = params.append('betriebskontakt_haendler_id[]', kontakt.kunde_id);
                }
            }

            if (this.mitEinstallgewicht) {
                params = params.set('mit_einstallgewicht', '1');
            }

            this.sub?.unsubscribe();
            const requests = targetGroupIds.map(gruppeId => this.stammdatenService.ladeEinstalldaten(gruppeId, params));
            this.sub = forkJoin(requests).subscribe({
                next: result => {
                    this.isLoading = false;
                    const byDate = new Map<string, GruppeTierEinstalldatum>();
                    for (const res of result || []) {
                        for (const item of res?.records || []) {
                            const key = this.toDateKey(item?.einstalldatum);
                            if (!key) {
                                continue;
                            }
                            const existing = byDate.get(key);
                            if (existing) {
                                existing.anzahl = Number(existing.anzahl || 0) + Number(item?.anzahl || 0);
                                if (item?.anzahl_mit_gewicht != null || existing.anzahl_mit_gewicht != null) {
                                    existing.anzahl_mit_gewicht =
                                        Number(existing.anzahl_mit_gewicht || 0) + Number(item?.anzahl_mit_gewicht || 0);
                                    existing.gesamtgewicht =
                                        Number(existing.gesamtgewicht || 0) + Number(item?.gesamtgewicht || 0);
                                }
                                continue;
                            }
                            byDate.set(key, item);
                        }
                    }
                    this.einstalldaten = Array.from(byDate.values());

                    const current = (this.selectedEinstalldatumCtrl.value || this.selectedEinstalldaten || []) as
                        | GruppeTierEinstalldatum[]
                        | undefined;
                    const preserved = (current || []).filter(selected =>
                        this.einstalldaten.some(option => this.compareGruppeTierEinstalldatum(selected, option)),
                    );
                    // Map to option object refs so mat-select compareWith stays stable after reload.
                    const preservedOptions = preserved
                        .map(selected =>
                            this.einstalldaten.find(option => this.compareGruppeTierEinstalldatum(selected, option)),
                        )
                        .filter((option): option is GruppeTierEinstalldatum => !!option);

                    if (this.einstalldaten.length === 0) {
                        this.label = 'keine Einstalldaten';
                        this.selectedEinstalldatumCtrl.setValue(
                            preservedOptions.length ? preservedOptions : undefined,
                            { emitEvent: false },
                        );
                        this.selectedEinstalldatumCtrl.disable({ emitEvent: false });
                    } else {
                        this.label = 'Einstalldatum';
                        this.selectedEinstalldatumCtrl.enable({ emitEvent: false });
                        this.selectedEinstalldatumCtrl.setValue(
                            preservedOptions.length ? preservedOptions : undefined,
                            { emitEvent: false },
                        );
                    }
                },
                error: err => {
                    this.isLoading = false;
                    this.messageService.errorMessage('Fehler beim Laden der Einstalldaten', err);
                },
            });
        }
    }
}
